
import { useState } from "react";
import { useLanguage } from "@/hooks/use-language";
import { useCreateReport } from "@/hooks/use-reports";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Loader2, Flag } from "lucide-react";

const REPORT_REASONS = [
  "wrong_answer",
  "wrong_question",
  "unclear_question",
  "duplicate",
  "typo",
  "other",
] as const;

interface ReportDialogProps {
  examSlug: string;
  questionNumber: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReportDialog({
  examSlug,
  questionNumber,
  open,
  onOpenChange,
}: ReportDialogProps) {
  const { t } = useLanguage();
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const createReportMutation = useCreateReport(examSlug);
  const submitting = createReportMutation.isPending;

  async function handleSubmit() {
    if (!reason) return;
    try {
      await createReportMutation.mutateAsync({
        question_number: questionNumber,
        reason,
        description: description || undefined,
      });
      setSubmitted(true);
    } catch {
      alert(t("common.error"));
    }
  }

  function handleClose(val: boolean) {
    if (!val) {
      setReason("");
      setDescription("");
      setSubmitted(false);
    }
    onOpenChange(val);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-red-500" />
            {t("report.title")}
          </DialogTitle>
          <DialogDescription>
            {t("report.desc")} (#{questionNumber})
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-6 text-center">
            <p className="text-sm text-green-600 dark:text-green-400">
              {t("report.success")}
            </p>
            <DialogClose
              render={<Button variant="outline" className="mt-4 cursor-pointer" />}
            >
              {t("common.confirm")}
            </DialogClose>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                {t("report.reason_label")}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {REPORT_REASONS.map((r) => (
                  <Button
                    key={r}
                    type="button"
                    variant={reason === r ? "default" : "outline"}
                    size="sm"
                    className="cursor-pointer text-xs"
                    onClick={() => setReason(r)}
                  >
                    {t(`report.reason_${r}`)}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                {t("report.detail_label")}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                placeholder={t("report.detail_placeholder")}
              />
            </div>
            <DialogFooter>
              <DialogClose
                render={<Button variant="outline" className="cursor-pointer" />}
              >
                {t("common.cancel")}
              </DialogClose>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !reason}
                className="cursor-pointer"
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Flag className="mr-2 h-4 w-4" />
                )}
                {t("report.submit")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
